import { Suspense } from 'react';
import { Outlet, RouteObject, RouterProvider, createBrowserRouter } from 'react-router';

import Capture from '@components/Capture';
import { LoadingScreen } from '@components/LoadingScreen';
import { GlobalMailpieceActionRenderer } from '@components/MailpieceActions/GlobalMailpieceActionRenderer';
import { SessionLost } from '@components/SessionLost';
import { CaptureProvider } from '@contexts/CaptureContext';
import { SettingsProvider } from '@contexts/SettingsContext';
import { DashboardLayout } from '@minimalsLayouts/dashboard';
import { SimpleLayout } from '@minimalsLayouts/simple';
import { MailPieceInboxType, Roles } from '@rdTypes/shared';
import { OpenCvProvider } from 'src/lib/OpenCvProvider';
import CommonError from 'src/pages/CommonError';

import { DefaultRecirect } from './DefaultRedirect';
import { AccountSetupGuard } from './guards/AccountSetupGuard';
import GuestGuard from './guards/GuestGuard';
import { RoleBasedGuard } from './guards/RoleBasedGuard';
import { UserProfileGuard } from './guards/UserProfileGuard';
import {
  AdministrationAccountSetup,
  AdministrationAutomationCreate,
  AdministrationAutomationEdit,
  AdministrationAutomations,
  AdministrationAutomationsConflicts,
  AdministrationCompany,
  AdministrationEditNotificationTemplates,
  AdministrationGroupsEdit,
  AdministrationGroupsList,
  AdministrationLocation,
  AdministrationLocations,
  AdministrationMailroom,
  AdministrationNotificationTemplates,
  AdministrationRoute,
  AdministrationRoutes,
  AdministrationUserCreate,
  AdministrationUserEdit,
  AdministrationUsersManagement,
  AdministrationUsersUpload,
  AuthCallback,
  AuthRegister,
  CreateCompany,
  Inbox,
  Mailpiece,
  MailroomActiveItems,
  MailroomActiveStorageGroups,
  MailroomDashboard,
  MailroomPendingRequests,
  MailroomReceiveSessions,
  MailroomSearch,
  MailroomStorageGroupContent,
  Onboarding,
  Page404,
  Page500,
  UserAccount,
} from './pages';
import AutomationConflictToast from 'src/components/AutomationConflictToast';
import { RDConnectProvider } from 'src/contexts/RDConnectContext';

export const anonimUserRoutes: RouteObject[] = [
  { path: '404', element: <Page404 /> },
  { path: '500', element: <Page500 /> },
  { path: 'session-timed-out', element: <SessionLost withLogout /> },
  { path: 'auth/callback', element: <AuthCallback /> },
  { path: '*', element: <LoadingScreen /> },
];

export const routesSection: RouteObject[] = [
  {
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <UserProfileGuard>
          <SettingsProvider>
            <RDConnectProvider>
              <OpenCvProvider>
                <GlobalMailpieceActionRenderer />
                <Outlet />
                <AutomationConflictToast />
              </OpenCvProvider>
            </RDConnectProvider>
          </SettingsProvider>
        </UserProfileGuard>
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: <DefaultRecirect />,
      },

      {
        path: 'administration/account-setup',
        element: <AdministrationAccountSetup />,
      },
      {
        element: (
          <AccountSetupGuard>
            <DashboardLayout>
              <Outlet />
            </DashboardLayout>
          </AccountSetupGuard>
        ),
        children: [
          {
            path: 'superadmin/onboarding',
            element: (
              <RoleBasedGuard accessibleRoles={[Roles.KTAdmin]}>
                <Onboarding />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'superadmin/create-company',
            element: (
              <RoleBasedGuard accessibleRoles={[Roles.KTAdmin]}>
                <CreateCompany />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'administration',
            element: (
              <RoleBasedGuard accessibleRoles={[Roles.Admin, Roles.KTAdmin]}>
                <Outlet />
              </RoleBasedGuard>
            ),
            children: [
              {
                path: 'company',
                element: <AdministrationCompany />,
              },
              {
                path: 'mailroom',
                element: <AdministrationMailroom />,
              },
              {
                path: 'users',
                children: [
                  { index: true, element: <AdministrationUsersManagement /> },
                  { path: 'create', element: <AdministrationUserCreate /> },
                  {
                    path: 'edit/:id',
                    element: <AdministrationUserEdit />,
                  },
                  { path: 'upload', element: <AdministrationUsersUpload /> },
                ],
              },
              {
                path: 'groups',
                children: [
                  { index: true, element: <AdministrationGroupsList /> },
                  { path: 'create', element: <AdministrationGroupsEdit /> },
                  {
                    path: ':id',
                    element: <AdministrationGroupsEdit />,
                  },
                ],
              },
              {
                path: 'automations',
                children: [
                  { index: true, element: <AdministrationAutomations /> },
                  { path: 'create', element: <AdministrationAutomationCreate /> },
                  { path: 'conflicts', element: <AdministrationAutomationsConflicts /> },
                  { path: 'edit/:id', element: <AdministrationAutomationEdit /> },
                ],
              },
              {
                path: 'delivery/routes',
                children: [
                  { index: true, element: <AdministrationRoutes /> },
                  { path: 'create', element: <AdministrationRoute /> },
                  { path: ':id', element: <AdministrationRoute /> },
                ],
              },
              {
                path: 'delivery/locations',
                children: [
                  { index: true, element: <AdministrationLocations /> },
                  { path: 'create', element: <AdministrationLocation /> },
                  { path: ':id', element: <AdministrationLocation /> },
                ],
              },
              {
                path: 'notifications',
                children: [
                  { index: true, element: <AdministrationNotificationTemplates /> },
                  { path: ':id', element: <AdministrationEditNotificationTemplates /> },
                ],
              },

              // AdministrationRoutes
            ],
          },

          {
            path: 'user/account',
            element: <UserAccount />,
          },

          {
            path: 'mailroom',
            element: (
              <RoleBasedGuard accessibleRoles={[Roles.Mailroom]}>
                <CaptureProvider>
                  <Outlet />
                  <Capture />
                </CaptureProvider>
              </RoleBasedGuard>
            ),
            children: [
              { path: 'dashboard', element: <MailroomDashboard /> },
              { path: 'receive', element: <MailroomReceiveSessions /> },
              { path: 'requests', element: <MailroomPendingRequests /> },
              {
                path: 'active',
                children: [
                  {
                    path: 'items',
                    element: <MailroomActiveItems />,
                  },
                  {
                    path: 'items/:mailpieceId',
                    element: <Mailpiece type={MailPieceInboxType.Mailroom} />,
                  },
                  {
                    path: 'storage-groups',
                    element: <MailroomActiveStorageGroups />,
                  },
                  {
                    path: 'storage-groups/:id',
                    element: <MailroomStorageGroupContent />,
                  },
                ],
              },
              { path: 'search', element: <MailroomSearch /> },
              { path: ':mailpieceCategory/:mailpieceId', element: <Mailpiece type={MailPieceInboxType.Mailroom} /> },
            ],
          },

          {
            path: 'inbox',
            element: (
              <RoleBasedGuard accessibleRoles={[Roles.Recipient]}>
                <Outlet />
              </RoleBasedGuard>
            ),
            children: [
              { path: ':label', element: <Inbox /> },
              { path: ':mailpieceCategory/:mailpieceId', element: <Mailpiece type={MailPieceInboxType.Recipient} /> },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'auth/register',
    element: (
      <GuestGuard>
        <AuthRegister />
      </GuestGuard>
    ),
  },
  {
    element: (
      <SimpleLayout>
        <Outlet />
      </SimpleLayout>
    ),
    children: [
      { path: '404', element: <Page404 /> },
      // TODO: Looks like this page is unused, discuss...
      { path: '500', element: <Page500 /> },
      { path: 'session-timed-out', element: <SessionLost withLogout /> },
      { path: 'auth/callback', element: <AuthCallback /> },
    ],
  },
  {
    path: '*',
    element: (
      <SimpleLayout>
        <Page404 />
      </SimpleLayout>
    ),
  },
];

const Router = () => {
  const router = createBrowserRouter([
    {
      Component: () => <Outlet />,
      errorElement: (import.meta.env.VITE_APP_ENVIRONMENT === 'production' ||
        import.meta.env.VITE_APP_ENVIRONMENT === 'staging') && <CommonError />,
      children: routesSection,
    },
  ]);

  return <RouterProvider router={router} />;
};

export default Router;
